import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play, Settings2, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { statusLabel, templateLabel } from '@/features/campaigns/types';
import { cn } from '@/lib/utils/cn';
import { usePromptUploads } from '@/features/voice-campaigns/api';
import {
  useCampaignsForUser,
  useDashboardMain,
  usePauseCampaign,
  useResellerCampaigns,
  useResumeCampaign,
  useStopCampaign,
} from '../api';
import { usePromptAudio } from '@/features/voice-library/use-audio-preview';

/* --------------------- Status badge helper --------------------- */

function statusBadge(code: number) {
  const label = statusLabel(code) || 'Unknown';
  const tone =
    code === 1
      ? 'bg-emerald-100 text-emerald-900'
      : code === 9 || code === 23
        ? 'bg-amber-100 text-amber-900'
        : code === 2 || code === 11
          ? 'bg-sky-100 text-sky-900'
          : code === 4 || code === 5 || code === 3
            ? 'bg-rose-100 text-rose-900'
            : 'bg-slate-200 text-slate-900';
  return (
    <Badge variant="outline" className={cn('px-1.5 py-0 text-[10px]', tone)}>
      {label}
    </Badge>
  );
}

/* --------------------- Today's running campaigns --------------------- */

export function RunningCampaignsWidget() {
  const dash = useDashboardMain();
  const rows = dash.data?.runningCampaigns ?? [];
  return (
    <RunningTable
      isLoading={dash.isLoading}
      rows={rows.map((r) => ({
        id: r.campaignId,
        name: r.campaignName,
        status: r.campaignStatus,
        template: r.templateId,
        uploaded: r.numbersUploaded,
        processed: r.numbersProcessed,
        connected: r.callsConnected,
      }))}
      empty="No campaigns running today."
    />
  );
}

/* --------------------- ADMIN: reseller live --------------------- */

export function ResellerRunningWidget() {
  const q = useResellerCampaigns();
  const rows = q.data ?? [];
  return (
    <RunningTable
      isLoading={q.isLoading}
      rows={rows.map((r) => ({
        id: r.campaignId,
        name: r.campaignName,
        owner: r.username,
        status: r.campaignStatus,
        template: r.templateId,
        uploaded: r.numbersUploaded,
        processed: r.numbersProcessed,
        connected: r.callsConnected,
        channels: r.channels,
      }))}
      showOwner
      showChannels
      empty="No reseller campaigns are live right now."
    />
  );
}

interface TableRow {
  id: string;
  name: string;
  owner?: string;
  status: number;
  template: number;
  uploaded: number;
  processed: number;
  connected: number;
  channels?: number;
}

function RunningTable({
  isLoading,
  rows,
  showOwner,
  showChannels,
  empty,
}: {
  isLoading: boolean;
  rows: TableRow[];
  showOwner?: boolean;
  showChannels?: boolean;
  empty: string;
}) {
  if (isLoading) {
    return <Skeleton className="h-32 w-full" />;
  }
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-muted-foreground">{empty}</p>
    );
  }
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5 text-left">Campaign</th>
            {showOwner && <th className="px-2 py-1.5 text-left">Owner</th>}
            <th className="px-2 py-1.5 text-left">Template</th>
            <th className="px-2 py-1.5 text-left">Status</th>
            {showChannels && (
              <th className="px-2 py-1.5 text-right">Chan</th>
            )}
            <th className="px-2 py-1.5 text-right">Uploaded</th>
            <th className="px-2 py-1.5 text-right">Dialed</th>
            <th className="px-2 py-1.5 text-right">Connected</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="px-2 py-1.5 font-medium">{r.name}</td>
              {showOwner && (
                <td className="px-2 py-1.5 text-muted-foreground">
                  {r.owner ?? '—'}
                </td>
              )}
              <td className="px-2 py-1.5 text-muted-foreground">
                {templateLabel(r.template) || '—'}
              </td>
              <td className="px-2 py-1.5">{statusBadge(r.status)}</td>
              {showChannels && (
                <td className="px-2 py-1.5 text-right font-mono">
                  {r.channels ?? 0}
                </td>
              )}
              <td className="px-2 py-1.5 text-right font-mono">
                {r.uploaded.toLocaleString()}
              </td>
              <td className="px-2 py-1.5 text-right font-mono">
                {r.processed.toLocaleString()}
              </td>
              <td className="px-2 py-1.5 text-right font-mono text-emerald-700">
                {r.connected.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------- SUPER_ADMIN: user drilldown --------------------- */

export function UserDrilldownWidget() {
  const dash = useDashboardMain();
  const userRows = dash.data?.dbUserWiseCampaigns ?? [];
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const userCampaigns = useCampaignsForUser(selectedUserId);

  const pause = usePauseCampaign();
  const resume = useResumeCampaign();
  const stop = useStopCampaign();

  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[240px_1fr]">
      {/* User list */}
      <div className="min-h-0 overflow-auto rounded-md border border-border">
        {dash.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : userRows.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No users with campaigns.
          </p>
        ) : (
          <ul>
            {userRows.map((u) => (
              <li key={u.userId}>
                <button
                  type="button"
                  onClick={() => setSelectedUserId(u.userId)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 border-b border-border px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted/40',
                    selectedUserId === u.userId && 'bg-primary/10',
                  )}
                >
                  <span className="truncate font-medium text-foreground">
                    {u.username}
                  </span>
                  <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                    {u.totalCampaigns}
                  </Badge>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Right pane — drilldown */}
      <div className="min-h-0 overflow-auto rounded-md border border-border">
        {!selectedUserId ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            Pick a user to see their campaigns.
          </p>
        ) : userCampaigns.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : (userCampaigns.data ?? []).length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">
            No campaigns for this user.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-muted/60 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Campaign</th>
                <th className="px-2 py-1.5 text-left">Status</th>
                <th className="px-2 py-1.5 text-right">Dialed</th>
                <th className="px-2 py-1.5 text-right">Connected</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(userCampaigns.data ?? []).map((c) => {
                const status = c.campaignStatus;
                const canPause = status === 1;
                const canResume = status === 9 || status === 23;
                const canStop = status === 1 || status === 9;
                return (
                  <tr key={c.campaignId} className="border-t border-border">
                    <td className="px-2 py-1.5 font-medium">
                      {c.campaignName}
                    </td>
                    <td className="px-2 py-1.5">{statusBadge(status)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">
                      {c.numbersProcessed.toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono text-emerald-700">
                      {c.callsConnected.toLocaleString()}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <CampaignActionsMenu
                        canPause={canPause}
                        canResume={canResume}
                        canStop={canStop}
                        onPause={() =>
                          pause.mutate(
                            { campaignId: c.campaignId },
                            {
                              onSuccess: (r) =>
                                toast.success(r?.message ?? 'Paused'),
                              onError: () => toast.error('Could not pause.'),
                            },
                          )
                        }
                        onResume={() =>
                          resume.mutate(
                            {
                              campaignId: c.campaignId,
                              userId: selectedUserId!,
                            },
                            {
                              onSuccess: (r) =>
                                toast.success(r?.message ?? 'Resumed'),
                              onError: () => toast.error('Could not resume.'),
                            },
                          )
                        }
                        onStop={() =>
                          stop.mutate(
                            { campaignId: c.campaignId },
                            {
                              onSuccess: (r) =>
                                toast.success(r?.message ?? 'Stopped'),
                              onError: () => toast.error('Could not stop.'),
                            },
                          )
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CampaignActionsMenu({
  canPause,
  canResume,
  canStop,
  onPause,
  onResume,
  onStop,
}: {
  canPause: boolean;
  canResume: boolean;
  canStop: boolean;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7">
          <Settings2 className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuLabel>Campaign actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onPause} disabled={!canPause}>
          <Pause className="h-3.5 w-3.5" />
          Pause
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onResume} disabled={!canResume}>
          <Play className="h-3.5 w-3.5" />
          Resume
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onStop}
          disabled={!canStop}
          className="text-destructive focus:text-destructive"
        >
          <StopCircle className="h-3.5 w-3.5" />
          Stop
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>
          <Settings2 className="h-3.5 w-3.5" />
          Edit channels (TBD)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* --------------------- Recent prompts --------------------- */

export function RecentPromptsWidget() {
  const prompts = usePromptUploads();
  const recent = useMemo(() => {
    const rows = prompts.data ?? [];
    return [...rows]
      .sort((a, b) => (b.promptId ?? 0) - (a.promptId ?? 0))
      .slice(0, 5);
  }, [prompts.data]);

  if (prompts.isLoading) return <Skeleton className="h-32 w-full" />;
  if (recent.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-muted-foreground">
        No prompts uploaded yet.{' '}
        <Link to="/voice-files" className="text-primary hover:underline">
          Upload one →
        </Link>
      </div>
    );
  }
  return (
    <ul className="space-y-1.5">
      {recent.map((p) => (
        <RecentPromptItem
          key={p.promptId}
          id={p.promptId}
          fileName={p.fileName ?? p.promptName ?? `#${p.promptId}`}
          url={(p as { promptUrl?: string }).promptUrl}
        />
      ))}
    </ul>
  );
}

function RecentPromptItem({
  id,
  fileName,
  url,
}: {
  id: number;
  fileName: string;
  url?: string;
}) {
  const audio = usePromptAudio(`recent-${id}`, url);
  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        onClick={audio.toggle}
        disabled={!url}
        aria-label={audio.playing ? 'Pause' : 'Play'}
      >
        {audio.playing ? (
          <Pause className="h-3 w-3" />
        ) : (
          <Play className="h-3 w-3" />
        )}
      </Button>
      <span className="min-w-0 flex-1 truncate text-xs">{fileName}</span>
    </li>
  );
}

// Re-export Input for unused-warning suppression in some configs.
void Input;
